/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, Input, OnChanges, OnInit, SimpleChanges, signal} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {StateService} from '@uirouter/core';
import * as _ from 'lodash';

import template from './query-stats-template-panel.component.html';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {LocalStorage} from '../../../components/session/local-storage';
import {LocalUserPermissions} from '../../../components/session/local-user-permissions';
import {LocalUserProfile} from '../../../components/session/local-user-profile';
import {Uuid} from '../../../components/util/uuid';
import {targetingTemplateSaveErrors} from '../../targeting/targeting/targeting-template-save.util';
import {
  applyQueryStatsTemplateData,
  captureQueryStatsTemplateData,
  queryStatsCategory,
  QueryStatsTemplateCategory,
  queryStatsTypeDefinition,
  queryStatsTypeLabel
} from './query-stats-template.util';
import {QueryStatsTemplateSelectionService} from './query-stats-template-selection.service';
import {QueryStatsTemplateApiService} from './query-stats-template-api.service';
import {QueryPageApiService} from './query-page-api.service';
import {APP_SHARED} from '../../common/app-shared';

interface QueryStatsTemplateGroup {
  key: string;
  label: string;
  templates: any[];
}

interface QueryStatsTemplateCache {
  cachedAt: number;
  networkId: string;
  templates: any[];
  userId: string;
}

const QUERY_STATS_TEMPLATE_CACHE_KEY = 'queryStatsTemplates';
const QUERY_STATS_TEMPLATE_CACHE_TTL_MS = 60 * 60 * 1000;

@Component({
  standalone: true,
  imports: [...APP_SHARED, FormsModule],
  selector: 'adn-query-stats-template-panel',
  template
})
export class QueryStatsTemplatePanelComponent implements OnInit, OnChanges {
  @Input() query: any;
  @Input() queryType = '';

  private readonly panelState = signal<any>({
    loading: false,
    saving: false,
    saved: false,
    view: 'LIST',
    templates: [],
    groups: [],
    expandedGroups: {},
    teams: [],
    model: undefined,
    saveErrors: []
  });
  private readState(key: string): any { return this.panelState()[key]; }
  private writeState(key: string, value: any): void {
    const state = this.panelState();
    if (!Object.is(state[key], value)) {
      this.panelState.set({...state, [key]: value});
    }
  }
  get loading(): boolean { return this.readState('loading'); }
  set loading(value: boolean) { this.writeState('loading', value); }
  get saving(): boolean { return this.readState('saving'); }
  set saving(value: boolean) { this.writeState('saving', value); }
  get saved(): boolean { return this.readState('saved'); }
  set saved(value: boolean) { this.writeState('saved', value); }
  get view(): 'LIST' | 'SAVE' | 'EDIT' { return this.readState('view'); }
  set view(value: 'LIST' | 'SAVE' | 'EDIT') { this.writeState('view', value); }
  get templates(): any[] { return this.readState('templates'); }
  set templates(value: any[]) { this.writeState('templates', value); }
  get groups(): QueryStatsTemplateGroup[] { return this.readState('groups'); }
  set groups(value: QueryStatsTemplateGroup[]) { this.writeState('groups', value); }
  get expandedGroups(): Record<string, boolean> { return this.readState('expandedGroups'); }
  set expandedGroups(value: Record<string, boolean>) { this.writeState('expandedGroups', value); }
  readonly maximumVisibleTemplates = 5;
  readonly maximumUncollapsedTemplates = 7;
  get teams(): any[] { return this.readState('teams'); }
  set teams(value: any[]) { this.writeState('teams', value); }
  get model(): any { return this.readState('model'); }
  set model(value: any) { this.writeState('model', value); }
  get saveErrors(): string[] { return this.readState('saveErrors'); }
  set saveErrors(value: string[]) { this.writeState('saveErrors', value); }

  constructor(
    private readonly queryStatsTemplateApi: QueryStatsTemplateApiService,
    private readonly queryPageApi: QueryPageApiService,
    @Inject(LocalUserPermissions) private readonly LocalUserPermissions: any,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    private readonly localUserProfile: LocalUserProfile,
    private readonly selection: QueryStatsTemplateSelectionService,
    private readonly $state: StateService
  ) {
  }

  ngOnInit(): void {
    this.loadTemplates();
    this.loadTeams();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.queryType) {
      this.expandedGroups = {};
      this.rebuildGroups();
      if (this.view === 'SAVE') {
        this.newTemplate();
      }
    }
  }

  newTemplate(): void {
    this.view = 'SAVE';
    this.saved = false;
    this.saveErrors = [];
    this.model = {
      name: queryStatsTypeLabel(this.queryType) + ' query',
      description: '',
      team: null
    };
  }

  edit(templateToEdit: any): void {
    this.view = 'EDIT';
    this.saved = false;
    this.saveErrors = [];
    this.model = _.cloneDeep(templateToEdit);
    this.model.team = this.findTeam(templateToEdit.team);
  }

  cancelEdit(): void {
    this.view = 'LIST';
    this.model = null;
    this.saveErrors = [];
  }

  save(): void {
    if (this.saving || !_.trim(this.model?.name)) {
      return;
    }

    this.saving = true;
    this.saved = false;
    this.saveErrors = [];
    const isNew = this.view === 'SAVE';
    const templateForSave = _.cloneDeep(this.model);
    templateForSave.id = templateForSave.id || Uuid.generate();
    templateForSave.name = _.trim(templateForSave.name);
    templateForSave.description = _.trim(templateForSave.description || '');
    templateForSave.team = templateForSave.team || null;
    if (isNew) {
      templateForSave.type = this.queryType;
      templateForSave.data = captureQueryStatsTemplateData(this.query);
    }

    this.queryStatsTemplateApi.save(templateForSave).subscribe({
      next: () => {
        this.saving = false;
        this.saved = true;
        this.view = 'LIST';
        this.model = null;
        this.loadTemplates(true);
      },
      error: (response) => {
        this.saving = false;
        this.saveErrors = targetingTemplateSaveErrors(response).map((error: string) => {
          return error.replace(/targeting template/gi, 'query template');
        });
      }
    });
  }

  archive(templateToArchive: any): void {
    if (this.saving) {
      return;
    }
    this.saving = true;
    this.queryStatsTemplateApi.archive(templateToArchive.id).subscribe({
      next: () => {
        this.saving = false;
        this.cancelEdit();
        this.loadTemplates(true);
      },
      error: (response) => {
        this.saving = false;
        this.saveErrors = targetingTemplateSaveErrors(response).map((error: string) => {
          return error.replace(/targeting template/gi, 'query template');
        });
      }
    });
  }

  use(templateToUse: any): void {
    if (templateToUse.type === this.queryType) {
      applyQueryStatsTemplateData(this.query, templateToUse.data);
      return;
    }

    const definition = queryStatsTypeDefinition(templateToUse.type);
    if (!definition) {
      return;
    }
    this.selection.select(templateToUse);
    this.$state.go(definition.state);
  }

  typeLabel(type: string): string {
    return queryStatsTypeLabel(type);
  }

  toggleGroup(groupKey: string): void {
    this.expandedGroups[groupKey] = !this.expandedGroups[groupKey];
  }

  compareTeams(team1: any, team2: any): boolean {
    return team1 && team2 ? team1.id === team2.id : team1 === team2;
  }

  private loadTemplates(forceRefresh = false): void {
    if (forceRefresh) {
      this.removeTemplateCache();
    } else {
      const cachedTemplates = this.readCachedTemplates();
      if (cachedTemplates !== null) {
        this.templates = cachedTemplates;
        this.rebuildGroups();
        return;
      }
    }

    this.loading = true;
    this.queryStatsTemplateApi.list().subscribe({
      next: (page) => {
        this.templates = page.results || [];
        this.cacheTemplates();
        this.loading = false;
        this.rebuildGroups();
      },
      error: () => {
        this.loading = false;
        this.templates = [];
        this.rebuildGroups();
      }
    });
  }

  private readCachedTemplates(): any[] | null {
    const context = this.templateCacheContext();
    if (!context) {
      return null;
    }

    const cache = LocalStorage.obtain().getItem(QUERY_STATS_TEMPLATE_CACHE_KEY) as QueryStatsTemplateCache;
    const cachedAt = Number(cache?.cachedAt);
    const cacheAge = Date.now() - cachedAt;
    if (!_.isArray(cache?.templates)
      || cache.userId !== context.userId
      || cache.networkId !== context.networkId
      || !_.isFinite(cachedAt)
      || cacheAge < 0
      || cacheAge >= QUERY_STATS_TEMPLATE_CACHE_TTL_MS) {
      return null;
    }
    return cache.templates;
  }

  private cacheTemplates(): void {
    const context = this.templateCacheContext();
    if (!context) {
      return;
    }
    try {
      LocalStorage.obtain().setItem(QUERY_STATS_TEMPLATE_CACHE_KEY, {
        cachedAt: Date.now(),
        networkId: context.networkId,
        templates: this.templates,
        userId: context.userId
      } satisfies QueryStatsTemplateCache);
    } catch (_error) {
      // The query list still works when local storage is unavailable or full.
    }
  }

  private removeTemplateCache(): void {
    try {
      LocalStorage.obtain().remove(QUERY_STATS_TEMPLATE_CACHE_KEY);
    } catch (_error) {
      // A failed cache invalidation should not block saving or deleting a query.
    }
  }

  private templateCacheContext(): {networkId: string; userId: string} | null {
    const networkId = this.LocalNetworkProfile.getNetworkId();
    const userId = this.localUserProfile.get('userId');
    return networkId && userId ? {networkId, userId} : null;
  }

  private loadTeams(): void {
    this.queryPageApi.listTeams().subscribe((page) => {
      const allTeams = page.results || [];
      const isViewAllObjects = this.LocalUserPermissions.hasNetwork('VIEW_ALL_OBJECTS');
      this.teams = isViewAllObjects ? allTeams : _.filter(allTeams, (team: any) => {
        return this.LocalUserPermissions.isPartOf(team.network.tagId || team.network.id, team.id);
      });
      if (this.model?.team) {
        this.model.team = this.findTeam(this.model.team);
      }
    });
  }

  private rebuildGroups(): void {
    const currentCategory = queryStatsCategory(this.queryType);
    const categoryOrder: Record<QueryStatsTemplateCategory, number> = {
      ADVERTISING: 0,
      PUBLISHING: 1,
      DATA: 2,
      OTHER: 3
    };

    const currentTemplates = _.orderBy(_.filter(this.templates, {type: this.queryType}), ['name'], ['asc']);
    const otherTypes = _.orderBy(_.uniq(_.map(_.reject(this.templates, {type: this.queryType}), 'type')), [
      (type: string) => queryStatsCategory(type) === currentCategory ? 0 : 1,
      (type: string) => categoryOrder[queryStatsCategory(type)],
      (type: string) => queryStatsTypeLabel(type)
    ], ['asc', 'asc', 'asc']);
    const otherTemplates = _.flatMap(otherTypes, (type: string) => {
      return _.orderBy(_.filter(this.templates, {type}), ['name'], ['asc']);
    });

    this.groups = [];
    if (currentTemplates.length) {
      this.groups.push({
        key: 'current',
        label: 'Saved ' + queryStatsTypeLabel(this.queryType) + ' Queries',
        templates: currentTemplates
      });
    }
    if (otherTemplates.length) {
      this.groups.push({
        key: 'other',
        label: currentTemplates.length ? 'Other Queries' : 'Saved Queries',
        templates: otherTemplates
      });
    }
  }

  private findTeam(team: any): any {
    const teamId = _.isString(team) ? team : team?.id;
    return _.find(this.teams, {id: teamId}) || team || null;
  }
}
